import { useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import styled from "styled-components";
import { Checkbox } from "./Checkbox";
import { RequiredBadge } from "./RequiredBadge";

type Props = {
  name: string;
  options: string[];
  label?: string;
  required?: boolean;
  defaultSelectedOptions?: string[];
  onChange?: (selectedOptions: string[]) => void;
};

export const CheckboxGroup: React.FC<Props> = ({
  name,
  label,
  options,
  required,
  defaultSelectedOptions = [],
  onChange,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>(
    defaultSelectedOptions,
  );

  const formContext = useFormContext();
  const { setValue } = formContext ?? {};

  useEffect(() => {
    setValue?.(name, selectedOptions, { shouldValidate: true });
  }, [selectedOptions, setValue, name]);

  const handleChange = useCallback(
    (checked: boolean, option: string) => {
      const newSelectedOptions = checked
        ? [...selectedOptions, option]
        : selectedOptions.filter((opt) => opt !== option);

      setSelectedOptions(newSelectedOptions);

      onChange?.(newSelectedOptions);
    },
    [selectedOptions, onChange],
  );

  return (
    <StyledContainer>
      <StyledHeader>
        {label && <StyledLabel>{label}</StyledLabel>}
        {required && <RequiredBadge />}
      </StyledHeader>

      <StyledCheckboxContainer>
        {options.map((option, i) => (
          <Checkbox
            key={`${name}-${i}`}
            name=""
            variant="empty"
            label={option}
            checked={defaultSelectedOptions.includes(option)}
            onChange={(checked) => handleChange(checked, option)}
          />
        ))}
      </StyledCheckboxContainer>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
`;

const StyledLabel = styled.label`
  font-size: 14px;
  font-weight: 400;
  line-height: 100%;
  margin-right: 5px;
`;

const StyledCheckboxContainer = styled.div`
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
